// Media helpers — pure functions shared by the post renderer, the gallery, and
// the composer's uploader. No DOM, no network, no global state.

// Filename to send with a multipart media upload. This is load-bearing rather
// than cosmetic: Pleroma only turns a multipart part into a file upload when
// its Content-Disposition carries a filename, and a part without one arrives
// as a bare string that crashes its uploader (a 500, not a 4xx). Browsers
// leave `name` empty on some Blob-derived files, so synthesize one from the
// MIME subtype when it's missing.
export function uploadFilename(file) {
  const name = String(file?.name || "").trim();
  if (name) return name;
  // "image/svg+xml" -> "svg"; "text/plain; charset=utf-8" -> "plain"
  const subtype = String(file?.type || "").split("/")[1] || "";
  const ext = subtype.split(/[;+]/)[0].replace(/[^a-z0-9]/gi, "").toLowerCase();
  return `upload.${ext && ext !== "octetstream" ? ext : "bin"}`;
}

export function formatBytes(bytes) {
  if (bytes == null || bytes < 0) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export function mediaSizeBytes(media) {
  const candidates = [
    media?.size,
    media?.meta?.size,
    media?.meta?.original?.size,
    media?.meta?.small?.size,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function mediaDimensions(media) {
  const candidates = [
    media?.meta?.original,
    media?.meta?.small,
    media?.meta,
    media,
  ];
  for (const c of candidates) {
    const w = Number(c?.width);
    const h = Number(c?.height);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(h) && h > 0) {
      return `${Math.round(w)}x${Math.round(h)}`;
    }
  }
  return "";
}

// Truncate a hash-like filename keeping the extension and showing start+end:
// e.g. "e10...25e.png" — allocates ~40% prefix, ~60% suffix within limit
export function truncateHash(name, max) {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || name.length <= max) return name;
  const ext = name.slice(dot);   // e.g. ".png"
  const stem = name.slice(0, dot);
  const prefixLen = Math.max(3, Math.floor(max * 0.4));
  const suffixLen = Math.max(3, max - ext.length - prefixLen - 3);
  return stem.slice(0, prefixLen) + "..." + stem.slice(-suffixLen) + ext;
}

export function urlPathSansQuery(url) {
  if (!url) return "";
  return String(url).split(/[?#]/)[0];
}

export function isLikelyVideoUrl(url) {
  return /\.(mp4|webm|ogg|mov|m4v)$/i.test(urlPathSansQuery(url));
}

export function isLikelyImageUrl(url) {
  return /\.(png|jpe?g|gif|webp|avif)$/i.test(urlPathSansQuery(url));
}

export function isLikelyPdfUrl(url) {
  const path = urlPathSansQuery(url);
  try {
    return /\.pdf$/i.test(decodeURIComponent(path));
  } catch {
    return /\.pdf$/i.test(path);
  }
}

// Mastodon represents document attachments as type "unknown". Mitra does
// that for PDFs and currently doesn't expose their stored MIME type in the
// Attachment entity, so the URL suffix is load-bearing there. Other servers
// may expose mime_type (or even use a document/file type), which we accept too.
export function isPdfAttachment(media) {
  const type = String(media?.type || "").toLowerCase();
  const mime = String(
    media?.mime_type
    || media?.meta?.mime_type
    || media?.meta?.original?.mime_type
    || "",
  ).toLowerCase().split(";", 1)[0].trim();
  return type === "pdf"
    || type === "application/pdf"
    || mime === "application/pdf"
    || isLikelyPdfUrl(mediaFullUrl(media));
}

export function isDocumentAttachment(media) {
  if (!media) return false;
  if (isPdfAttachment(media)) return true;
  const type = String(media.type || "").toLowerCase();
  const mime = String(media.mime_type || "").toLowerCase();
  return type === "unknown"
    || type === "document"
    || type === "file"
    || type.startsWith("application/")
    || mime.startsWith("application/");
}

export function preferredVideoPoster(media) {
  const preview = media?.preview_url || "";
  const full = media?.url || "";

  if (preview && !isLikelyVideoUrl(preview)) return preview;

  const candidates = [
    media?.meta?.small?.url,
    media?.meta?.original?.url,
    media?.meta?.preview_url,
    media?.meta?.small?.preview_url,
    media?.meta?.original?.preview_url,
  ];
  for (const c of candidates) {
    if (!c) continue;
    if (isLikelyImageUrl(c)) return c;
    if (c !== full && c !== preview && !isLikelyVideoUrl(c)) return c;
  }
  return "";
}

export function mediaFullUrl(media) {
  return media?.url || media?.preview_url || "";
}

// Return `url` only if it's safe to use as a link/resource target: an
// http(s)/mailto URL, or a relative/anchor reference. Returns "" for anything
// else (javascript:, data:, vbscript:, …). Using a scheme allowlist — rather
// than blocklisting "javascript:" — also defeats control-char obfuscation like
// `java\tscript:` that browsers still execute.
export function safeUrl(url) {
  const s = String(url == null ? "" : url).trim();
  if (!s) return "";
  if (/^(\/|#|\.|\?)/.test(s)) return s;            // relative / anchor / query
  if (/^(https?:|mailto:)/i.test(s)) return s;      // allowed absolute schemes
  return "";
}
